import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "api/api";

// Material Components
import { Grid, TextField, Button, Card } from "@mui/material";
import Alert from "react-bootstrap/Alert";

// CKEditor 5
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// Custom Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import "../../styles/custom.css";
import "bootstrap/dist/css/bootstrap.min.css";

function EditPageForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState({ type: "", message: "" });
  const ImgBaseUrl = "https://api.sicamobile.com";
  const [formData, setFormData] = useState({
    title: "",
    banner: null,
    bannerPreview: "",
    description: "",
    seo_title: "",
    seo_keyword: "",
    seo_desc: "",
    isActive: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`admin/page/edit/${id}`);
        const data = response.data.data;

        setFormData((prev) => ({
          ...prev,
          title: data.title || "",
          description: data.description || "",
          seo_title: data.seo_title || "",
          seo_keyword: data.seo_keyword || "",
          seo_desc: data.seo_desc || "",
          banner: null,
          bannerPreview: data.banner ? `${ImgBaseUrl}/${data.banner}` : "",
        }));
      } catch (error) {
        console.error("Failed to fetch CMS data:", error);
        setAlert({ type: "danger", message: "Failed to load CMS data." });
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({
        ...formData,
        banner: file,
        bannerPreview: URL.createObjectURL(file),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    form.append("title", formData.title);
    form.append("description", formData.description);
    form.append("seo_title", formData.seo_title);
    form.append("seo_keyword", formData.seo_keyword);
    form.append("seo_desc", formData.seo_desc);
    if (formData.banner) {
      form.append("banner", formData.banner);
    }

    try {
      const response = await api.post(`admin/page/update/${id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const result = response.data;
      if (result.status) {
        setAlert({ type: "success", message: result.message || "CMS updated successfully!" });
        setTimeout(() => navigate("page"), 3000);
      } else {
        setAlert({ type: "danger", message: result.message || "Update failed!" });
      }
    } catch (error) {
      console.error("Update error:", error);
      setAlert({ type: "danger", message: "Server error during update." });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Edit CMS Page
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                {alert.message && (
                  <Alert variant={alert.type} onClose={() => setAlert({})} dismissible>
                    {alert.message}
                  </Alert>
                )}

                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <label style={{ marginBottom: "5px", display: "block" }}>Description</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={formData.description}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          setFormData((prev) => ({ ...prev, description: data }));
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="SEO Title"
                        name="seo_title"
                        value={formData.seo_title}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="SEO Keyword"
                        name="seo_keyword"
                        value={formData.seo_keyword}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="SEO Description"
                        name="seo_desc"
                        value={formData.seo_desc}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <label className="d-block mb-2">Banner image (1600x800)</label>
                      <input
                        type="file"
                        name="banner"
                        onChange={handleFileChange}
                        accept="image/*"
                        className="form-control"
                      />
                      {formData.bannerPreview && (
                        <img
                          src={formData.bannerPreview}
                          alt="Preview"
                          className="mt-2"
                          style={{ maxWidth: "100%", height: "auto", borderRadius: "8px" }}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Button type="submit" className="btn" variant="contained" color="info">
                        Update
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditPageForm;
